// File: src/components/admin/GeoLocationsView.js
// admin view for the unified geo-target registry.
// Both Google Ads and Microsoft Advertising are first-class — each has its own
// status card with Preview/Import, plus an "Import both" action and a lookup
// tool (with a platform filter) that shows both platforms' ids side by side.
// Mounts at /geo. Talks to the admin worker via adminApi.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Card, CardContent, Stack, Typography, Grid, Button, Alert,
    CircularProgress, Divider, TextField, MenuItem, Table, TableBody,
    TableCell, TableHead, TableRow, Chip,
} from '@mui/material';
import {
    Refresh as RefreshIcon, Public as PublicIcon, Search as SearchIcon,
    CloudDownload as ImportIcon, Visibility as PreviewIcon,
    Link as LinkIcon, LinkOff as LinkOffIcon, CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';

const PLATFORM_LABELS = { google: 'Google Ads', microsoft: 'Microsoft Advertising' };

function formatTime(iso) {
    if (!iso) return 'Never';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/** Poll a background import job until it reaches a terminal state. */
async function pollImportJob(jobId, onTick, { interval = 1500, timeout = 120000 } = {}) {
    const start = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const job = await adminApi.getGeoImportJob(jobId);
        if (onTick) onTick(job);
        if (['completed', 'partial', 'failed'].includes(job.status)) return job;
        if (Date.now() - start > timeout) throw new Error('Import timed out');
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, interval));
    }
}

function PlatformCard({ platform, status, busy, onPreview, onImport, preview, importDisabled, importHint }) {
    const label = PLATFORM_LABELS[platform];
    return (
        <Card variant="outlined" data-testid={`geo-card-${platform}`} sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <PublicIcon color="action" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Records: <strong data-testid={`geo-${platform}-count`}>{status?.recordCount ?? 0}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Last import: {formatTime(status?.lastImportAt)}
                </Typography>
                {preview && (
                    <Alert severity="info" sx={{ mt: 1.5 }} data-testid={`geo-${platform}-preview`}>
                        Preview — would write {preview.recordCount} records
                        {' '}({preview.merged} merged · {preview.googleOnly} Google-only · {preview.msOnly} MS-only · {preview.deprecated} deprecated)
                    </Alert>
                )}
                {importHint && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1.5 }} data-testid={`geo-${platform}-import-hint`}>
                        {importHint}
                    </Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                        size="small" variant="outlined" startIcon={<PreviewIcon />}
                        disabled={busy} onClick={onPreview}
                        data-testid={`geo-${platform}-preview-btn`}
                    >
                        Preview
                    </Button>
                    <Button
                        size="small" variant="contained" startIcon={<ImportIcon />}
                        disabled={busy || importDisabled} onClick={onImport}
                        data-testid={`geo-${platform}-import-btn`}
                    >
                        Import
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function GeoLocationsView() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previews, setPreviews] = useState({});
    const [warning, setWarning] = useState(null);

    // Microsoft connection state (geo MS import needs an admin connection).
    const [msConn, setMsConn] = useState(null); // null=loading, { connected }
    const [connBusy, setConnBusy] = useState(false);

    // Lookup tool state
    const [q, setQ] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [platformFilter, setPlatformFilter] = useState('');
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);

    const loadSummary = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.getGeoSummary();
            setSummary(data);
        } catch (e) {
            setError(handleAdminApiError(e).message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadConnections = useCallback(async () => {
        try {
            const ms = await adminApi.getIntegrationStatus('microsoft-ads');
            setMsConn(ms);
        } catch (e) {
            setMsConn({ connected: false, error: handleAdminApiError(e).message });
        }
    }, []);

    useEffect(() => { loadSummary(); loadConnections(); }, [loadSummary, loadConnections]);

    const connectMicrosoft = useCallback(async () => {
        try {
            setConnBusy(true); setError(null);
            const { authUrl } = await adminApi.getIntegrationAuthUrl('microsoft-ads');
            window.location.href = authUrl; // provider returns to /integrations/oauth-callback/microsoft-ads
        } catch (e) {
            setError(handleAdminApiError(e).message);
            setConnBusy(false);
        }
    }, []);

    const disconnectMicrosoft = useCallback(async () => {
        try {
            setConnBusy(true); setError(null);
            await adminApi.disconnectIntegration('microsoft-ads');
            await loadConnections();
        } catch (e) {
            setError(handleAdminApiError(e).message);
        } finally {
            setConnBusy(false);
        }
    }, [loadConnections]);

    const preview = useCallback(async (platforms) => {
        try {
            setBusy(true); setError(null);
            const res = await adminApi.importGeo({ platforms, dryRun: true });
            const key = platforms.length === 1 ? platforms[0] : 'both';
            setPreviews((prev) => ({ ...prev, [key]: res.stats }));
        } catch (e) {
            setError(handleAdminApiError(e).message);
        } finally {
            setBusy(false);
        }
    }, []);

    const runImport = useCallback(async (platforms) => {
        try {
            setBusy(true); setError(null); setWarning(null); setProgress({ status: 'running', platforms });
            const { jobId } = await adminApi.importGeo({ platforms });
            const job = await pollImportJob(jobId, (j) => setProgress(j));
            setProgress(job);
            if (job.status === 'failed') {
                setError(job.error || 'Import failed');
            } else {
                // 'partial' → some platforms imported, others failed (e.g. Microsoft not connected).
                if (job.status === 'partial' && job.platformErrors) {
                    setWarning(Object.values(job.platformErrors).join(' '));
                }
                setPreviews({});
                await Promise.all([loadSummary(), loadConnections()]);
            }
        } catch (e) {
            setError(handleAdminApiError(e).message || e.message);
        } finally {
            setBusy(false);
        }
    }, [loadSummary, loadConnections]);

    const doSearch = useCallback(async () => {
        if (!countryCode) { setSearchError('Country code is required (e.g. US, DE).'); return; }
        try {
            setSearching(true); setSearchError(null);
            const res = await adminApi.searchGeo({
                q, countryCode: countryCode.toUpperCase(),
                platform: platformFilter || undefined,
            });
            setResults(res.results || []);
        } catch (e) {
            setSearchError(handleAdminApiError(e).message);
        } finally {
            setSearching(false);
        }
    }, [q, countryCode, platformFilter]);

    if (loading) {
        return <Box sx={{ p: 4, textAlign: 'center' }} data-testid="geo-loading"><CircularProgress /></Box>;
    }

    const platforms = summary?.platforms || {};
    const coverage = summary?.coverage;

    return (
        <Box sx={{ p: 3 }} data-testid="geo-view">
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>Geo Target Registry</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Google Ads & Microsoft Advertising location codes · last import {formatTime(summary?.lastImportAt)}
                        {summary?.lastImportBy ? ` by ${summary.lastImportBy}` : ''}
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={loadSummary} disabled={busy} data-testid="geo-refresh">
                    Refresh
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="geo-error">{error}</Alert>}
            {warning && <Alert severity="warning" sx={{ mb: 2 }} data-testid="geo-warning">{warning}</Alert>}

            { /* platform connections — Microsoft's catalogue is fetched via the
                authenticated MS API, so an admin connection is required to import it. */}
            <Card variant="outlined" sx={{ mb: 2 }} data-testid="geo-connections">
                <CardContent>
                    <Typography variant="overline" color="text.secondary">Platform connections</Typography>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
                        <Typography sx={{ minWidth: 200, fontWeight: 600 }}>Microsoft Advertising</Typography>
                        {msConn === null ? (
                            <CircularProgress size={16} />
                        ) : msConn.connected ? (
                            <>
                                <Chip icon={<CheckCircleIcon />} color="success" size="small" label="Connected" data-testid="ms-conn-status" />
                                <Button size="small" variant="outlined" startIcon={<LinkOffIcon />}
                                    disabled={connBusy} onClick={disconnectMicrosoft} data-testid="ms-disconnect-btn">
                                    Disconnect
                                </Button>
                            </>
                        ) : (
                            <>
                                <Chip size="small" label="Not connected" data-testid="ms-conn-status" />
                                <Button size="small" variant="contained" startIcon={<LinkIcon />}
                                    disabled={connBusy} onClick={connectMicrosoft} data-testid="ms-connect-btn">
                                    Connect
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                    Required to import Microsoft locations.
                                </Typography>
                            </>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <PlatformCard
                        platform="google" status={platforms.google} busy={busy} preview={previews.google}
                        onPreview={() => preview(['google'])} onImport={() => runImport(['google'])}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PlatformCard
                        platform="microsoft" status={platforms.microsoft} busy={busy} preview={previews.microsoft}
                        onPreview={() => preview(['microsoft'])} onImport={() => runImport(['microsoft'])}
                        importDisabled={msConn !== null && !msConn.connected}
                        importHint={msConn !== null && !msConn.connected ? 'Connect Microsoft Advertising above to import.' : null}
                    />
                </Grid>
            </Grid>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                <Button
                    variant="contained" startIcon={<ImportIcon />} disabled={busy}
                    onClick={() => runImport(['google', 'microsoft'])} data-testid="geo-import-both-btn"
                >
                    Import both
                </Button>
                <Button variant="outlined" startIcon={<PreviewIcon />} disabled={busy}
                    onClick={() => preview(['google', 'microsoft'])} data-testid="geo-preview-both-btn">
                    Preview both
                </Button>
                {busy && progress?.status === 'running' && (
                    <Stack direction="row" spacing={1} alignItems="center" data-testid="geo-progress">
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                            Importing {(progress.platforms || []).map((p) => PLATFORM_LABELS[p]).join(' + ')}…
                        </Typography>
                    </Stack>
                )}
            </Stack>

            {previews.both && (
                <Alert severity="info" sx={{ mt: 2 }} data-testid="geo-preview-both">
                    Preview (both) — would write {previews.both.recordCount} records
                    {' '}({previews.both.merged} merged · {previews.both.googleOnly} Google-only · {previews.both.msOnly} MS-only).
                </Alert>
            )}

            {coverage && (
                <Card variant="outlined" sx={{ mt: 2 }} data-testid="geo-coverage">
                    <CardContent>
                        <Typography variant="overline" color="text.secondary">Coverage</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            <Chip label={`Matched (both): ${coverage.merged}`} color="success" />
                            <Chip label={`Google-only: ${coverage.googleOnly}`} />
                            <Chip label={`Microsoft-only: ${coverage.msOnly}`} />
                            <Chip label={`Deprecated: ${coverage.deprecated}`} color="warning" />
                            <Chip label={`Total: ${coverage.recordCount}`} variant="outlined" />
                            <Chip label={`Countries: ${summary?.countryCount ?? 0}`} variant="outlined" />
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Divider sx={{ my: 3 }} />

            {/* ── Lookup / browse tool ── */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Lookup</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                    label="Name contains" size="small" value={q}
                    onChange={(e) => setQ(e.target.value)}
                    inputProps={{ 'data-testid': 'geo-search-q' }}
                />
                <TextField
                    label="Country code" size="small" value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    sx={{ width: 140 }} inputProps={{ 'data-testid': 'geo-search-country' }}
                />
                <TextField
                    select label="Platform" size="small" value={platformFilter}
                    onChange={(e) => setPlatformFilter(e.target.value)}
                    sx={{ width: 180 }} SelectProps={{ 'data-testid': 'geo-search-platform' }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="google">Google</MenuItem>
                    <MenuItem value="microsoft">Microsoft</MenuItem>
                </TextField>
                <Button
                    variant="contained" startIcon={<SearchIcon />} onClick={doSearch}
                    disabled={searching} data-testid="geo-search-btn"
                >
                    Search
                </Button>
            </Stack>

            {searchError && <Alert severity="warning" sx={{ mt: 2 }} data-testid="geo-search-error">{searchError}</Alert>}

            {results && (
                <Table size="small" sx={{ mt: 2 }} data-testid="geo-results">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell>Google criterion ID</TableCell>
                            <TableCell>Microsoft location ID</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.length === 0 && (
                            <TableRow><TableCell colSpan={5}>No matches.</TableCell></TableRow>
                        )}
                        {results.map((r) => (
                            <TableRow key={r.canonicalId} data-testid="geo-result-row">
                                <TableCell>{r.name}{r.status === 'deprecated' ? ' (deprecated)' : ''}</TableCell>
                                <TableCell>{r.type}</TableCell>
                                <TableCell>{r.countryCode || '—'}</TableCell>
                                <TableCell data-testid="geo-result-google">{r.platforms?.google?.criterionId || '—'}</TableCell>
                                <TableCell data-testid="geo-result-ms">{r.platforms?.microsoft?.locationId || '—'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}
